import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Header } from "../../../components/header";
import { Loading } from "../../../components/loading";
import { CardHeader } from "../../../components/cards";
import { SendIcon } from "../../../components/icons";
import { useCustomer } from "../hooks";
import { CustomerAddressesList } from "../../customer-addresses/components";
import { ContactsList } from "../../contacts/components";
import { VisitsList } from "../../visits/components";
import { OrdersList } from "../../orders/components";
import { CustomerReport } from "../../reports/components";
import { PriceRuleForm, PriceRulesList } from "../../price-rules/components";
import { GivenCansList } from "../../given-cans/components";
import { ProspectionForm, ProspectionsList } from "../../prospections/components";
import { MailMessagesList, SendMailMessageForm } from "../../mail-messages/components";
import { TasksList } from "../../tasks/components";

type ICustomerParams = {
  id: string;
};

export const Customer: React.FC = () => {
  const { id } = useParams<ICustomerParams>();
  const navigate = useNavigate();
  const [priceRulesKey, setPriceRulesKey] = React.useState(0);
  const [prospectionsKey, setProspectionsKey] = React.useState(0);
  const [mailMessagesKey, setMailMessagesKey] = React.useState(0);

  useEffect(() => {
    if (!id) {
      navigate("/");
    }
  }, []);

  const { data, loading } = useCustomer(+id!);

  if (loading) return <Loading />;

  const customer = data?.customer?.result;

  return (
    <>
      <Header
        title="Client"
        subtitle={
          <span className="flex items-center gap-2">
            {customer?.name}
            {customer && (
              customer.isComplete ? (
                <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                  Fiche complète
                </span>
              ) : (
                <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                  Fiche incomplète
                </span>
              )
            )}
          </span>
        }
        buttons={[
          {
            actionText: "Modifier",
            bgColor: "indigo",
            textColor: "white",
            link: `/customer/${id}/update`,
            icon: <SendIcon />,
          },
        ]}
      />
      <div className="main-container">
        <div className="card mb-4">
          <CardHeader title="Rapport" />
          <CustomerReport customerId={+id!} />
        </div>

        <div className="section">
          <div className="element">
            <div className="card">
              <CardHeader title="Informations générales" />
              <div className="w-full mt-3">
                <p className="label">Nom</p>
                <input className="input w-full" value={customer?.name} disabled />
              </div>
              <div className="flex">
                <div className="w-1/2 mr-1">
                  <div className="w-full mt-3">
                    <p className="label">Email</p>
                    <input className="input w-full" value={customer?.email ?? ""} disabled />
                  </div>
                </div>
                <div className="w-1/2 ml-1">
                  <div className="w-full mt-3">
                    <p className="label">Téléphone</p>
                    <input className="input w-full" value={customer?.phone ?? ""} disabled />
                  </div>
                </div>
              </div>
              <div className="w-full mt-3">
                <p className="label">Siren</p>
                <input className="input w-full" value={customer?.siren ?? ""} disabled />
              </div>
              <div className="w-full mt-3">
                <p className="label">Catégorie</p>
                <input
                  className="input w-full"
                  value={customer?.category?.name ?? ""}
                  disabled
                />
              </div>
            </div>

            <div className="card mt-4">
              <CardHeader
                title="Adresses"
                button={{
                  title: "Nouvelle adresse",
                  url: `/customer-address/create?customerId=${id}`,
                }}
              />
              <CustomerAddressesList customerId={+id!} />
            </div>

            <div className="card mt-4">
              <CardHeader
                title="Contacts"
                button={{
                  title: "Nouveau contact",
                  url: `/contact/create?customerId=${id}`,
                }}
              />
              <ContactsList customerId={+id!} />
            </div>

            <div className="card mt-4">
              <CardHeader title="Tarifs négociés" />
              <PriceRuleForm
                customerId={+id!}
                onCompleted={() => setPriceRulesKey((k) => k + 1)}
              />
              <div className="mt-4">
                <PriceRulesList key={priceRulesKey} customerId={+id!} />
              </div>
            </div>

            <div className="card mt-4">
              <CardHeader title="Échantillons distribués" />
              <GivenCansList customerId={+id!} />
            </div>
          </div>

          <div className="element">
            <div className="card">
              <CardHeader
                title="Visites"
                button={{
                  title: "Nouvelle visite",
                  url: `/visit/create?customerId=${id}`,
                }}
              />
              <VisitsList customerId={+id!} />
            </div>

            <div className="card mt-4">
              <CardHeader
                title="Commandes"
                button={{
                  title: "Nouvelle commande",
                  url: `/order/create?customerId=${id}`,
                }}
              />
              <OrdersList customerId={+id!} />
            </div>

            <div className="card mt-4">
              <CardHeader title="Prospections" />
              <ProspectionForm
                customerId={+id!}
                onCompleted={() => setProspectionsKey((k) => k + 1)}
              />
              <div className="mt-4">
                <ProspectionsList key={prospectionsKey} customerId={+id!} />
              </div>
            </div>

            <div className="card mt-4">
              <CardHeader title="Tâches liées" />
              <TasksList customerId={+id!} />
            </div>

            <div className="card mt-4">
              <CardHeader title="Emails" />
              <SendMailMessageForm
                customerId={+id!}
                defaultTo={customer?.email ?? ""}
                onCompleted={() => setMailMessagesKey((k) => k + 1)}
              />
              <div className="mt-4">
                <MailMessagesList key={mailMessagesKey} customerId={+id!} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
